// React
import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useState } from "react";

// @mui material components
import {
    Autocomplete,
    Card,
    CircularProgress,
    Fade,
    Grid,
    Icon,
    Modal,
    TextField,
    Tooltip,
    Switch,
    FormControlLabel,
    Select,
    MenuItem,
} from "@mui/material";

// Apollo
import { gql, useMutation, useQuery } from "@apollo/client";

// UI components
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";

// Context
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";
import { getUproColor } from "utils/colors";

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxHeight: "90%",
    width: "70%",
    overflowY: "auto",
    p: 0,
    borderRadius: 3,
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
};

export default function ModalAddProductoPresupuesto({ open, handleClose, idPresupuesto, refetch }) {
    const { handleSnackbar } = useContext(MessageManager);
    const { confirmar } = useContext(ConfirmarContext);
    const [productoSel, setProductoSel] = useState(null);
    const [cantidad, setCantidad] = useState(1);
    const [precio, setPrecio] = useState(0);
    const [nombre, setNombre] = useState("");
    const [itemsServicio, setItemsServicio] = useState("");
    const [detallesBuffer, setDetallesBuffer] = useState([]);
    const [detallesProdExpanded, setDetallesProdExpanded] = useState(false);
    const [detallesPersExpanded, setDetallesPersExpanded] = useState(false);
    const [incluirPersonal, setIncluirPersonal] = useState(false);
    const [empleadoSel, setEmpleadoSel] = useState(null);
    const [horasEmpleado, setHorasEmpleado] = useState(0);
    const [searchEmpleado, setSearchEmpleado] = useState("");

    const [search, setSearch] = useState("");
    const { data: productosData, loading: loadingProductos } = useQuery(
        gql`
      query getProductos(
        $pagination: PaginationInput
        $order: Order
        $search: String
        $filter: JSON
        $sinIngredientes: Boolean
        $materiaPrimaDeshabilitada: Boolean
      ) {
        productos(
          pagination: $pagination
          order: $order
          search: $search
          filter: $filter
          sinIngredientes: $sinIngredientes
          materiaPrimaDeshabilitada: $materiaPrimaDeshabilitada
        ) {
          id
          nombre
          precioCarta
          precioCosto
        }
      }
    `,
        {
            variables: {
                pagination: { page: 1, limit: 50 },
                order: { field: "nombre", order: "ASC" },
                search: search || null,
                filter: {
                    esIngrediente: false,
                },
                sinIngredientes: false,
                materiaPrimaDeshabilitada: false,
            },
            fetchPolicy: "network-only",
            skip: !open,
        }
    );

    // Empleados
    const GET_USUARIOS = gql`
    query getUsuarios($search: String, $page: Int, $order: Order) {
      paginationInfo {
        pages
        total
      }
      usuarios(
        search: $search
        pagination: { page: $page, limit: 10 }
        order: $order
        filter: { tipoUser: ["Empleado", "Mozo", "UproDay"] }
      ) {
        id
        nombre
        apellido
        tipoUser
      }
    }
  `;

    const { data: empleadosData, loading: loadingEmpleados } = useQuery(GET_USUARIOS, {
        variables: {
            search: searchEmpleado || null,
            page: 1,
            order: { field: "apellido", order: "ASC" },
        },
        fetchPolicy: "network-only",
        skip: !open || !incluirPersonal,
    });

    const { data: dataItems, loading: loadingItems } = useQuery(
        gql`
      query getItemsEvento {
        itemsPresupuesto {
          id
          nombre
        }
      }
    `
    );

    const [replaceDetalles, { loading }] = useMutation(
        gql`
      mutation asignarPresupuestoEventoDetalles(
        $idPresupuesto: ID!
        $detalles: [PresupuestoEventoDetalleInput]!
        $personal: Boolean
      ) {
        asignarPresupuestoEventoDetalles(
          idPresupuesto: $idPresupuesto
          detalles: $detalles
          personal: $personal
        ) {
          id
        }
      }
    `
    );

    const [deleteDetalle] = useMutation(
        gql`
      mutation deletePresupuestoEventoDetalle($id: ID!) {
        deletePresupuestoEventoDetalle(id: $id)
      }
    `
    );

    const { data: preEvent, refetch: refetchDetalles } = useQuery(
        gql`
      query getPresupuestoModalAddProductoPresupuesto($id: ID!) {
        presupuestoEvento(id: $id) {
          id
          personal
          detalles {
            id
            nombre
            producto {
              id
              nombre
            }
            idEmpleado
            idItem
            cargaHorariaEmpleado
            cantidadAsignada
            precioUnitario
          }
        }
      }
    `,
        {
            variables: { id: idPresupuesto },
            fetchPolicy: "network-only",
            skip: !open || !idPresupuesto,
        }
    );

    const [clearPersonalAsignado] = useMutation(
        gql`
      mutation clearPersonalAsignadoDePresupuesto($idPresupuesto: ID!) {
        clearPersonalAsignadoDePresupuesto(idPresupuesto: $idPresupuesto)
      }
    `
    );

    useEffect(() => {
        if (preEvent?.presupuestoEvento) {
            setIncluirPersonal(preEvent?.presupuestoEvento.personal);
            // Preload buffer con los detalles actuales
            const curr = preEvent?.presupuestoEvento?.detalles || [];
            setDetallesBuffer(
                curr.map((d) => ({
                    id: d.id,
                    idProducto: d.producto?.id || undefined,
                    nombre: d.nombre,
                    cantidadAsignada: d.cantidadAsignada ?? 0,
                    precioUnitario: d.precioUnitario ?? 0,
                    idEmpleado: d.idEmpleado || undefined,
                    cargaHorariaEmpleado: d.cargaHorariaEmpleado ?? 0,
                    idItem: d.idItem || null,
                }))
            );
        }
    }, [preEvent]);

    useEffect(() => {
        if (open) {
            // Limpiar sólo los inputs efímeros del formulario; no tocar flags que reflejan el backend
            setProductoSel(null);
            setCantidad(1);
            setPrecio(0);
            setNombre("");
            setSearch("");
            setEmpleadoSel(null);
            setHorasEmpleado(0);
            setSearchEmpleado("");
        } else {
            // Al cerrar, colapsar secciones de listado para una apertura limpia
            setDetallesProdExpanded(false);
            setDetallesPersExpanded(false);
        }
    }, [open]);

    // Si el presupuesto ya tiene detalles, al abrir el modal expandimos automáticamente
    useEffect(() => {
        if (!open) return;
        const hasProducts = (detallesBuffer || []).some((d) => !d?.idEmpleado);
        const hasPersonal = (detallesBuffer || []).some((d) => !!d?.idEmpleado);
        if (hasProducts) setDetallesProdExpanded(true);
        if (hasPersonal) setDetallesPersExpanded(true);
    }, [open, detallesBuffer]);

    const options = useMemo(() => productosData?.productos || [], [productosData]);
    const productItems = useMemo(
        () =>
            (detallesBuffer || [])
                .filter((d) => !d?.idEmpleado)
                .sort((a, b) => {
                    const idA = a?.idItem;
                    const idB = b?.idItem;

                    // Si ambos no tienen idItem, se consideran iguales
                    if (idA == null && idB == null) return 0;
                    // Si A no tiene idItem, va después
                    if (idA == null) return 1;
                    // Si B no tiene idItem, va después
                    if (idB == null) return -1;
                    // Ambos tienen idItem → ordenar numéricamente
                    return idA - idB;
                }),
        [detallesBuffer]
    );


    const personalItems = useMemo(
        () => (detallesBuffer || []).filter((d) => !!d?.idEmpleado),
        [detallesBuffer]
    );
    const totalProductos = useMemo(
        () =>
            productItems.reduce(
                (acc, d) => acc + (Number(d?.cantidadAsignada) || 0) * (Number(d?.precioUnitario) || 0),
                0
            ),
        [productItems]
    );
    const totalHoras = useMemo(
        () => personalItems.reduce((acc, d) => acc + (Number(d?.cargaHorariaEmpleado) || 0), 0),
        [personalItems]
    );

    const handleDeleteDetalle = (detalle) => {
        confirmar({
            title: "Eliminar detalle",
            message: `¿Eliminar ${detalle?.nombre || detalle?.producto?.nombre || "detalle"
                } del presupuesto?`,
            icon: "warning",
            func: async () => {
                try {
                    await deleteDetalle({ variables: { id: detalle.id } });
                    handleSnackbar("Detalle eliminado", "success");
                    await refetchDetalles();
                    if (typeof refetch === "function") refetch();
                } catch (e) {
                    handleSnackbar("Error al eliminar detalle", "error");
                }
            },
        });
    };

    const addDetalleToBuffer = () => {
        const nuevo = {
            idProducto: productoSel?.id || undefined,
            nombre: nombre || productoSel?.nombre || undefined,
            cantidadAsignada: Number(cantidad) || 1,
            precioUnitario: Number(precio) || productoSel?.precioCarta || 0,
            idItem: itemsServicio || null,
        };
        if (!nuevo.nombre) {
            handleSnackbar("Debe ingresar un nombre o seleccionar un producto", "warning");
            return;
        }
        if ((nuevo.cantidadAsignada || 0) <= 0) {
            handleSnackbar("La cantidad debe ser mayor a 0", "warning");
            return;
        }
        if ((nuevo.precioUnitario || 0) < 0) {
            handleSnackbar("El precio no puede ser negativo", "warning");
            return;
        }
        setDetallesBuffer((prev) => [...prev, nuevo]);
        setProductoSel(null);
        setCantidad(1);
        setPrecio(0);
        setItemsServicio("");
        setNombre("");
    };

    const addEmpleadoDetalleToBuffer = () => {
        const nuevo = {
            idEmpleado: empleadoSel?.id || undefined,
            cargaHorariaEmpleado: Number(horasEmpleado) || 0,
            nombre:
                nombre ||
                (empleadoSel
                    ? `${empleadoSel?.apellido ? empleadoSel.apellido + ", " : ""}${empleadoSel?.nombre || "Empleado"
                    }`
                    : undefined),
        };
        if (!nuevo.idEmpleado) {
            handleSnackbar("Debe seleccionar un empleado", "warning");
            return;
        }
        if ((nuevo.cargaHorariaEmpleado || 0) < 0) {
            handleSnackbar("Las horas no pueden ser negativas", "warning");
            return;
        }
        setDetallesBuffer((prev) => [...prev, nuevo]);
        setEmpleadoSel(null);
        setHorasEmpleado(0);
        setItemsServicio("");
        setNombre("");
    };

    const removeDetalleFromBuffer = (index) => {
        setDetallesBuffer((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        if (!idPresupuesto) return;
        try {
            const hasPersonal = (detallesBuffer || []).some((d) => !!d?.idEmpleado);
            const detalles = (detallesBuffer || []).map((d) => ({
                idProducto: d.idProducto,
                idEmpleado: d.idEmpleado,
                cargaHorariaEmpleado: Number(d.cargaHorariaEmpleado) || 0,
                nombre: d.nombre,
                cantidadAsignada: Number(d.cantidadAsignada) || 0,
                precioUnitario: Number(d.precioUnitario) || 0,
                idItem: d.idItem || null,
            }));
            await replaceDetalles({ variables: { idPresupuesto, detalles, personal: hasPersonal } });
            await refetchDetalles();
            if (typeof refetch === "function") refetch();
            handleSnackbar("Detalles actualizados", "success");
        } catch (e) {
            handleSnackbar("Error al actualizar detalles", "error");
        }
    };

    const isDisabled = loading || !idPresupuesto;

    const handleToggleIncluirPersonal = (checked) => {
        if (checked) {
            setIncluirPersonal(true);
            return;
        }
        // Al desactivar
        if ((personalItems || []).length > 0) {
            confirmar({
                title: "Quitar personal",
                message:
                    "Hay personal asignado a este presupuesto ¿desea quitarlos?\nEsta acción eliminará todas las asignaciones de personal.",
                icon: "warning",
                func: async () => {
                    try {
                        await clearPersonalAsignado({ variables: { idPresupuesto } });
                        await refetchDetalles();
                        setEmpleadoSel(null);
                        setHorasEmpleado(0);
                        setDetallesPersExpanded(false);
                        setIncluirPersonal(false);
                        handleSnackbar("Se quitó el personal asignado", "success");
                    } catch (e) {
                        handleSnackbar("No se pudo quitar el personal asignado", "error");
                        setIncluirPersonal(true);
                    }
                },
            });
        } else {
            // No hay personal: sólo desactivar y resetear UI
            setEmpleadoSel(null);
            setHorasEmpleado(0);
            setDetallesPersExpanded(false);
            setIncluirPersonal(false);
        }
    };

    return (
        <Modal
            aria-labelledby="modal-add-producto-presupuesto"
            open={open}
            onClose={handleClose}
            closeAfterTransition
        >
            <Fade in={open}>
                <Card
                    sx={{
                        ...style,
                        width: incluirPersonal ? "70%" : style.width,
                    }}
                >
                    {/* Header del modal */}
                    <SoftBox
                        sx={{
                            borderBottom: "1px solid",
                            borderColor: "grey.200",
                            p: 2,
                            borderRadius: "12px 12px 0 0",
                        }}
                    >
                        <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                            <SoftBox display="flex" alignItems="center">
                                <Icon sx={{ fontSize: "1.5rem", mr: 1, color: "text.secondary" }}>
                                    add_shopping_cart
                                </Icon>
                                <SoftTypography variant="h6" fontWeight="medium">
                                    Asignar Producto
                                </SoftTypography>
                            </SoftBox>
                            <Tooltip title="Cerrar" placement="top">
                                <Icon
                                    fontSize="medium"
                                    onClick={handleClose}
                                    sx={{
                                        cursor: "pointer",
                                        color: "text.secondary",
                                        "&:hover": { color: "text.primary", transform: "scale(1.1)" },
                                        transition: "all 0.2s ease",
                                    }}
                                >
                                    close
                                </Icon>
                            </Tooltip>
                        </SoftBox>
                    </SoftBox>

                    {/* Contenido del modal */}
                    <SoftBox p={3}>
                        <SoftBox mb={3}>
                            <Grid container spacing={3}>
                                <Grid
                                    item
                                    xs={12}
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                        color="text"
                                    >
                                        Configurar detalle
                                    </SoftTypography>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={incluirPersonal}
                                                onChange={(e) => handleToggleIncluirPersonal(e.target.checked)}
                                            />
                                        }
                                        label="Incluir personal"
                                    />
                                </Grid>

                                {/* Columna izquierda: Productos */}
                                <Grid item xs={12} sm={incluirPersonal ? 6 : 12}>
                                    <SoftBox
                                        sx={{
                                            p: 2,
                                            border: "1px solid",
                                            borderColor: "grey.200",
                                            borderRadius: 2,
                                            bgcolor: "grey.50",
                                        }}
                                    >
                                        <SoftTypography
                                            variant="caption"
                                            color="text"
                                            fontWeight="bold"
                                            sx={{ mb: 1, display: "block" }}
                                        >
                                            Productos
                                        </SoftTypography>
                                        <SoftBox mb={1}>
                                            <SoftTypography
                                                component="label"
                                                variant="caption"
                                                fontWeight="bold"
                                                color="text"
                                            >
                                                <Icon sx={{ fontSize: "1rem", mr: 0.5, verticalAlign: "middle" }}>
                                                    search
                                                </Icon>
                                                Producto (opcional)
                                            </SoftTypography>
                                        </SoftBox>
                                        <Autocomplete
                                            value={productoSel}
                                            options={options}
                                            getOptionLabel={(option) => option?.nombre || ""}
                                            loading={loadingProductos}
                                            noOptionsText="No hay resultados"
                                            onInputChange={(e, value) => setSearch(value)}
                                            onChange={(e, newValue) => {
                                                setProductoSel(newValue);
                                                setPrecio(newValue?.precioCarta || 0);
                                                if (newValue?.nombre) setNombre(newValue.nombre);
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Buscar producto..."
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: 2,
                                                            "&:hover fieldset": {
                                                                borderColor: "#667eea",
                                                            },
                                                            "&.Mui-focused fieldset": {
                                                                borderColor: "#667eea",
                                                            },
                                                        },
                                                    }}
                                                />
                                            )}
                                            sx={{
                                                "& .MuiAutocomplete-paper": {
                                                    borderRadius: 2,
                                                    boxShadow: "0 8px 25px -8px rgba(0, 0, 0, 0.15)",
                                                },
                                            }}
                                        />
                                        <SoftBox mt={2}>
                                            <SoftBox mb={1}>
                                                <SoftTypography
                                                    component="label"
                                                    variant="caption"
                                                    fontWeight="bold"
                                                    color="text"
                                                >
                                                    <Icon sx={{ fontSize: "1rem", mr: 0.5, verticalAlign: "middle" }}>
                                                        edit
                                                    </Icon>
                                                    Nombre del detalle
                                                </SoftTypography>
                                            </SoftBox>
                                            <SoftInput
                                                placeholder={productoSel?.nombre || "Ingrese el nombre del detalle"}
                                                type="text"
                                                name="nombre"
                                                value={nombre}
                                                onChange={(e) => setNombre(e.target.value)}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: 2,
                                                    },
                                                }}
                                            />
                                        </SoftBox>
                                        <Grid container spacing={2} mt={1}>
                                            <Grid item xs={12} sm={incluirPersonal ? 6 : 3}>
                                                <SoftBox mb={1}>
                                                    <SoftTypography
                                                        component="label"
                                                        variant="caption"
                                                        fontWeight="bold"
                                                        color="text"
                                                    >
                                                        <Icon sx={{ fontSize: "1rem", mr: 0.5, verticalAlign: "middle" }}>
                                                            inventory
                                                        </Icon>
                                                        Cantidad
                                                    </SoftTypography>
                                                </SoftBox>
                                                <SoftInput
                                                    placeholder="1"
                                                    type="number"
                                                    name="cantidad"
                                                    value={cantidad}
                                                    onChange={(e) => setCantidad(e.target.value)}
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: 2,
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={incluirPersonal ? 6 : 3}>
                                                <SoftBox mb={1}>
                                                    <SoftTypography
                                                        component="label"
                                                        variant="caption"
                                                        fontWeight="bold"
                                                        color="text"
                                                    >
                                                        <Icon sx={{ fontSize: "1rem", mr: 0.5, verticalAlign: "middle" }}>
                                                            inventory
                                                        </Icon>
                                                        Servicio
                                                    </SoftTypography>
                                                </SoftBox>
                                                <Select
                                                    value={itemsServicio}
                                                    onChange={(e) => setItemsServicio(e.target.value)}
                                                    displayEmpty
                                                    inputProps={{ "aria-label": "Mes" }}
                                                >
                                                    <MenuItem value="">
                                                        <em>Ninguno</em>
                                                    </MenuItem>
                                                    {(dataItems?.itemsPresupuesto || []).map(({ nombre, id }) => (
                                                        <MenuItem key={`${id}-${nombre}`} value={id}>
                                                            {nombre}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </Grid>
                                            <Grid item xs={12} sm={incluirPersonal ? 6 : 3}>
                                                <SoftBox mb={1}>
                                                    <SoftTypography
                                                        component="label"
                                                        variant="caption"
                                                        fontWeight="bold"
                                                        color="text"
                                                    >
                                                        <Icon sx={{ fontSize: "1rem", mr: 0.5, verticalAlign: "middle" }}>
                                                            price_check
                                                        </Icon>
                                                        Costo del producto
                                                    </SoftTypography>
                                                </SoftBox>
                                                <SoftInput
                                                    placeholder="0.00"
                                                    type="number"
                                                    name="costoProducto"
                                                    value={productoSel?.precioCosto || 0}
                                                    disabled
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: 2,
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={incluirPersonal ? 6 : 3}>
                                                <SoftBox mb={1}>
                                                    <SoftTypography
                                                        component="label"
                                                        variant="caption"
                                                        fontWeight="bold"
                                                        color="text"
                                                    >
                                                        <Icon sx={{ fontSize: "1rem", mr: 0.5, verticalAlign: "middle" }}>
                                                            attach_money
                                                        </Icon>
                                                        Precio unitario
                                                    </SoftTypography>
                                                    <Tooltip title="Precio por unidad que se asignará al presupuesto" placement="top">
                                                        <Icon sx={{
                                                            fontSize: "1rem", mr: 0.5, verticalAlign: "middle",
                                                            '&:hover': { color: getUproColor('uproPink'), cursor: 'pointer', transform: 'scale(1.1)' },
                                                            transition: 'all 0.2s ease',
                                                        }}>
                                                            info_outline
                                                        </Icon>
                                                    </Tooltip>
                                                </SoftBox>
                                                <SoftInput
                                                    placeholder="0.00"
                                                    type="number"
                                                    name="precio"
                                                    value={precio}
                                                    onChange={(e) => setPrecio(e.target.value)}
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: 2,
                                                        },
                                                    }}
                                                />
                                            </Grid>

                                        </Grid>
                                        <SoftBox display="flex" justifyContent="flex-end" mt={2}>
                                            <SoftButton color="info" onClick={addDetalleToBuffer}>
                                                <Icon sx={{ mr: 1 }}>add</Icon>
                                                Agregar detalle
                                            </SoftButton>
                                        </SoftBox>
                                    </SoftBox>
                                </Grid>

                                {/* Columna derecha: Personal */}
                                {incluirPersonal && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <SoftBox
                                                sx={{
                                                    p: 2,
                                                    border: "1px solid",
                                                    borderColor: "grey.200",
                                                    borderRadius: 2,
                                                    bgcolor: "grey.50",
                                                }}
                                            >
                                                <SoftTypography
                                                    variant="caption"
                                                    color="text"
                                                    fontWeight="bold"
                                                    sx={{ mb: 1, display: "block" }}
                                                >
                                                    Personal
                                                </SoftTypography>
                                                <SoftBox mb={1}>
                                                    <SoftTypography
                                                        component="label"
                                                        variant="caption"
                                                        fontWeight="bold"
                                                        color="text"
                                                    >
                                                        <Icon sx={{ fontSize: "1rem", mr: 0.5, verticalAlign: "middle" }}>
                                                            person_search
                                                        </Icon>
                                                        Empleado
                                                    </SoftTypography>
                                                </SoftBox>
                                                <Autocomplete
                                                    value={empleadoSel}
                                                    options={empleadosData?.usuarios || []}
                                                    getOptionLabel={(option) =>
                                                        `${option?.apellido ? option.apellido + ", " : ""}${option?.nombre || ""
                                                        }`
                                                    }
                                                    loading={loadingEmpleados}
                                                    noOptionsText="No hay resultados"
                                                    onInputChange={(e, value) => setSearchEmpleado(value)}
                                                    onChange={(e, newValue) => setEmpleadoSel(newValue)}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            placeholder="Buscar empleado..."
                                                            sx={{
                                                                "& .MuiOutlinedInput-root": {
                                                                    borderRadius: 2,
                                                                    "&:hover fieldset": { borderColor: "#667eea" },
                                                                    "&.Mui-focused fieldset": { borderColor: "#667eea" },
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <SoftBox mt={2}>
                                                    <SoftBox mb={1}>
                                                        <SoftTypography
                                                            component="label"
                                                            variant="caption"
                                                            fontWeight="bold"
                                                            color="text"
                                                        >
                                                            <Icon sx={{ fontSize: "1rem", mr: 0.5, verticalAlign: "middle" }}>
                                                                schedule
                                                            </Icon>
                                                            Carga horaria (hs)
                                                        </SoftTypography>
                                                    </SoftBox>
                                                    <SoftInput
                                                        placeholder="0"
                                                        type="number"
                                                        name="horasEmpleado"
                                                        value={horasEmpleado}
                                                        onChange={(e) => setHorasEmpleado(e.target.value)}
                                                        sx={{
                                                            "& .MuiOutlinedInput-root": { borderRadius: 2 },
                                                        }}
                                                    />
                                                    <SoftBox display="flex" justifyContent="flex-end" mt={2}>
                                                        <SoftButton
                                                            color="primary"
                                                            variant="outlined"
                                                            onClick={addEmpleadoDetalleToBuffer}
                                                        >
                                                            <Icon sx={{ mr: 1 }}>person_add</Icon>
                                                            Agregar personal
                                                        </SoftButton>
                                                    </SoftBox>
                                                </SoftBox>
                                            </SoftBox>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </SoftBox>

                        {/* Botones de acción */}
                        <SoftBox
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="center"
                            gap={2}
                            sx={{
                                borderTop: "1px solid",
                                borderColor: "grey.200",
                                pt: 3,
                                mt: 2,
                            }}
                        >
                            <SoftButton
                                color="secondary"
                                variant="outlined"
                                onClick={handleClose}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontWeight: "medium",
                                    px: 3,
                                    "&:hover": {
                                        transform: "translateY(-1px)",
                                        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.12)",
                                    },
                                    transition: "all 0.2s ease",
                                }}
                            >
                                <Icon sx={{ fontWeight: "light", mr: 1 }}>cancel</Icon>
                                Cancelar
                            </SoftButton>
                            <SoftButton
                                color="uproGreen"
                                disabled={isDisabled}
                                onClick={handleSave}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontWeight: "medium",
                                    px: 3,
                                    minWidth: 120,
                                    "&:hover": {
                                        transform: "translateY(-1px)",
                                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                                    },
                                    "&:disabled": {
                                        opacity: 0.6,
                                    },
                                    transition: "all 0.2s ease",
                                }}
                            >
                                {loading ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : (
                                    <Icon sx={{ fontWeight: "light", mr: 1 }}>save</Icon>
                                )}
                                Guardar cambios
                            </SoftButton>
                        </SoftBox>

                        {/* Listas de detalles actuales: Productos y Personal */}
                        <SoftBox mt={4}>
                            {/* Productos Agregados */}
                            <SoftBox
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{
                                    borderBottom: "2px solid",
                                    borderColor: "grey.200",
                                    pb: 2,
                                    mb: 2,
                                    cursor: "pointer",
                                    "&:hover": { backgroundColor: "grey.50" },
                                    transition: "background-color 0.2s ease",
                                }}
                                onClick={() => setDetallesProdExpanded(!detallesProdExpanded)}
                            >
                                <SoftBox display="flex" alignItems="center">
                                    <Icon sx={{ fontSize: "1.5rem", mr: 1, color: "success.main" }}>
                                        shopping_cart
                                    </Icon>
                                    <SoftTypography variant="h6" fontWeight="bold">
                                        Productos agregados ({productItems?.length || 0})
                                    </SoftTypography>
                                    <Icon
                                        sx={{
                                            ml: 1,
                                            color: "text.secondary",
                                            transform: detallesProdExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                            transition: "transform 0.3s ease",
                                        }}
                                    >
                                        expand_more
                                    </Icon>
                                </SoftBox>
                                <SoftBox
                                    sx={{
                                        background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
                                        color: "white",
                                        px: 2,
                                        py: 1,
                                        borderRadius: 2,
                                        boxShadow: "0 2px 8px rgba(76, 175, 80, 0.3)",
                                    }}
                                >
                                    <SoftTypography variant="subtitle2" fontWeight="bold">
                                        Total: $ {totalProductos.toLocaleString()}
                                    </SoftTypography>
                                </SoftBox>
                            </SoftBox>
                            {detallesProdExpanded && (
                                <SoftBox
                                    sx={{
                                        maxHeight: "300px",
                                        overflowY: "auto",
                                        mb: 3,
                                        animation: "fadeIn 0.3s ease-in-out",
                                        "@keyframes fadeIn": {
                                            "0%": { opacity: 0, transform: "translateY(-10px)" },
                                            "100%": { opacity: 1, transform: "translateY(0)" },
                                        },
                                    }}
                                >
                                    {productItems?.length === 0 ? (
                                        <SoftBox
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="center"
                                            justifyContent="center"
                                            sx={{ py: 3, textAlign: "center", color: "grey.500" }}
                                        >
                                            <Icon sx={{ fontSize: "3rem", mb: 1, opacity: 0.5 }}>inventory_2</Icon>
                                            <SoftTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
                                                No hay productos agregados aún
                                            </SoftTypography>
                                        </SoftBox>
                                    ) : (
                                        <Grid container spacing={2}>
                                            {productItems.map((d, index) => (
                                                <Grid key={`prod-${index}`} item xs={12}>
                                                    <SoftBox
                                                        sx={{
                                                            background: index % 2 === 0 ? "grey.50" : "white",
                                                            p: 2,
                                                            borderRadius: 2,
                                                            border: "1px solid",
                                                            borderColor: "grey.200",
                                                            "&:hover": {
                                                                borderColor: "info.main",
                                                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                                                            },
                                                            transition: "all 0.2s ease",
                                                        }}
                                                    >
                                                        <SoftBox
                                                            display="flex"
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                        >
                                                            <SoftBox display="flex" alignItems="center" flex={1}>
                                                                <Icon sx={{ fontSize: "1.2rem", mr: 1.5, color: "success.main" }}>
                                                                    shopping_cart
                                                                </Icon>
                                                                <SoftBox>
                                                                    <SoftTypography variant="button" fontWeight="medium">
                                                                        {d?.nombre || `Detalle #${index + 1}`}
                                                                    </SoftTypography>
                                                                    <SoftBox display="flex" alignItems="center" mt={0.5}>
                                                                        <SoftTypography variant="caption" color="text" sx={{ mr: 2 }}>
                                                                            <Icon
                                                                                sx={{
                                                                                    fontSize: "0.8rem",
                                                                                    mr: 0.5,
                                                                                    verticalAlign: "middle",
                                                                                }}
                                                                            >
                                                                                inventory
                                                                            </Icon>
                                                                            Cant: {d?.cantidadAsignada ?? 0}
                                                                        </SoftTypography>
                                                                        <SoftTypography variant="caption" color="text" sx={{ mr: 2 }}>
                                                                            <Icon
                                                                                sx={{
                                                                                    fontSize: "0.8rem",
                                                                                    mr: 0.5,
                                                                                    verticalAlign: "middle",
                                                                                }}
                                                                            >
                                                                                attach_money
                                                                            </Icon>
                                                                            $ {Number(d?.precioUnitario ?? 0).toLocaleString()}
                                                                        </SoftTypography>
                                                                        {d.idItem && (<SoftTypography variant="caption" color="text">
                                                                            <Icon
                                                                                sx={{
                                                                                    fontSize: "0.8rem",
                                                                                    mr: 0.5,
                                                                                    verticalAlign: "middle",
                                                                                }}
                                                                            >
                                                                                inventory
                                                                            </Icon>
                                                                            {((dataItems?.itemsPresupuesto || []).find(item => item.id == d?.idItem)?.nombre ?? "")}
                                                                        </SoftTypography>)}
                                                                    </SoftBox>
                                                                </SoftBox>
                                                            </SoftBox>
                                                            <SoftBox display="flex" alignItems="center">
                                                                <SoftTypography
                                                                    variant="button"
                                                                    fontWeight="bold"
                                                                    color="success"
                                                                    sx={{ mr: 2 }}
                                                                >
                                                                    ${" "}
                                                                    {(
                                                                        (d?.cantidadAsignada ?? 0) * (d?.precioUnitario ?? 0)
                                                                    ).toLocaleString()}
                                                                </SoftTypography>
                                                                <Tooltip title="Eliminar detalle" placement="top">
                                                                    <Icon
                                                                        sx={{
                                                                            cursor: "pointer",
                                                                            color: "error.main",
                                                                            "&:hover": { color: "error.dark", transform: "scale(1.1)" },
                                                                            transition: "all 0.2s ease",
                                                                            fontSize: "1.2rem",
                                                                        }}
                                                                        onClick={() =>
                                                                            removeDetalleFromBuffer(detallesBuffer.indexOf(d))
                                                                        }
                                                                    >
                                                                        delete
                                                                    </Icon>
                                                                </Tooltip>
                                                            </SoftBox>
                                                        </SoftBox>
                                                    </SoftBox>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </SoftBox>
                            )}

                            {/* Personal Asignado */}
                            <SoftBox
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{
                                    borderBottom: "2px solid",
                                    borderColor: "grey.200",
                                    pb: 2,
                                    mb: 2,
                                    cursor: "pointer",
                                    "&:hover": { backgroundColor: "grey.50" },
                                    transition: "background-color 0.2s ease",
                                }}
                                onClick={() => setDetallesPersExpanded(!detallesPersExpanded)}
                            >
                                <SoftBox display="flex" alignItems="center">
                                    <Icon sx={{ fontSize: "1.5rem", mr: 1, color: "info.main" }}>group</Icon>
                                    <SoftTypography variant="h6" fontWeight="bold">
                                        Personal asignado ({personalItems?.length || 0})
                                    </SoftTypography>
                                    <Icon
                                        sx={{
                                            ml: 1,
                                            color: "text.secondary",
                                            transform: detallesPersExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                            transition: "transform 0.3s ease",
                                        }}
                                    >
                                        expand_more
                                    </Icon>
                                </SoftBox>
                                <SoftBox
                                    sx={{
                                        background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
                                        color: "white",
                                        px: 2,
                                        py: 1,
                                        borderRadius: 2,
                                        boxShadow: "0 2px 8px rgba(33, 150, 243, 0.3)",
                                    }}
                                >
                                    <SoftTypography variant="subtitle2" fontWeight="bold">
                                        Total horas: {totalHoras.toLocaleString()} hs
                                    </SoftTypography>
                                </SoftBox>
                            </SoftBox>
                            {detallesPersExpanded && (
                                <SoftBox
                                    sx={{
                                        maxHeight: "300px",
                                        overflowY: "auto",
                                        mb: 1,
                                        animation: "fadeIn 0.3s ease-in-out",
                                        "@keyframes fadeIn": {
                                            "0%": { opacity: 0, transform: "translateY(-10px)" },
                                            "100%": { opacity: 1, transform: "translateY(0)" },
                                        },
                                    }}
                                >
                                    {personalItems?.length === 0 ? (
                                        <SoftBox
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="center"
                                            justifyContent="center"
                                            sx={{ py: 3, textAlign: "center", color: "grey.500" }}
                                        >
                                            <Icon sx={{ fontSize: "3rem", mb: 1, opacity: 0.5 }}>group_off</Icon>
                                            <SoftTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
                                                No hay personal asignado aún
                                            </SoftTypography>
                                        </SoftBox>
                                    ) : (
                                        <Grid container spacing={2}>
                                            {personalItems.map((d, index) => (
                                                <Grid key={`pers-${index}`} item xs={12}>
                                                    <SoftBox
                                                        sx={{
                                                            background: index % 2 === 0 ? "grey.50" : "white",
                                                            p: 2,
                                                            borderRadius: 2,
                                                            border: "1px solid",
                                                            borderColor: "grey.200",
                                                            "&:hover": {
                                                                borderColor: "info.main",
                                                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                                                            },
                                                            transition: "all 0.2s ease",
                                                        }}
                                                    >
                                                        <SoftBox
                                                            display="flex"
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                        >
                                                            <SoftBox display="flex" alignItems="center" flex={1}>
                                                                <Icon sx={{ fontSize: "1.2rem", mr: 1.5, color: "info.main" }}>
                                                                    person
                                                                </Icon>
                                                                <SoftBox>
                                                                    <SoftTypography variant="button" fontWeight="medium">
                                                                        {d?.nombre || `Empleado #${d.idEmpleado}`}
                                                                    </SoftTypography>
                                                                    <SoftBox display="flex" alignItems="center" mt={0.5}>
                                                                        <SoftTypography variant="caption" color="text">
                                                                            <Icon
                                                                                sx={{
                                                                                    fontSize: "0.8rem",
                                                                                    mr: 0.5,
                                                                                    verticalAlign: "middle",
                                                                                }}
                                                                            >
                                                                                schedule
                                                                            </Icon>
                                                                            Horas: {Number(d?.cargaHorariaEmpleado ?? 0)}
                                                                        </SoftTypography>
                                                                    </SoftBox>
                                                                </SoftBox>
                                                            </SoftBox>
                                                            <SoftBox display="flex" alignItems="center">
                                                                <Tooltip title="Eliminar detalle" placement="top">
                                                                    <Icon
                                                                        sx={{
                                                                            cursor: "pointer",
                                                                            color: "error.main",
                                                                            "&:hover": { color: "error.dark", transform: "scale(1.1)" },
                                                                            transition: "all 0.2s ease",
                                                                            fontSize: "1.2rem",
                                                                        }}
                                                                        onClick={() =>
                                                                            removeDetalleFromBuffer(detallesBuffer.indexOf(d))
                                                                        }
                                                                    >
                                                                        delete
                                                                    </Icon>
                                                                </Tooltip>
                                                            </SoftBox>
                                                        </SoftBox>
                                                    </SoftBox>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </SoftBox>
                            )}
                        </SoftBox>
                    </SoftBox>
                </Card>
            </Fade>
        </Modal>
    );
}

ModalAddProductoPresupuesto.propTypes = {
    open: PropTypes.bool,
    handleClose: PropTypes.func,
    idPresupuesto: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    refetch: PropTypes.func,
};
